import {
  Button,
  DialogDisclosure,
  Heading,
  HeadingLevel,
} from "@ariakit/react";
import { DialogTrigger, sleep } from "@indietabletop/appkit";
import { interactiveText } from "@indietabletop/appkit/common.css.ts";
import { useEffect, useRef } from "react";
import { button, lacquer } from "../../common/common.css.ts";
import { database } from "../../database.ts";
import { Gang } from "../../engine/Gang.ts";
import { BuildStep, StandaloneBuildStep, WrapUpStep } from "./BuildStep.tsx";
import { Cap } from "./Cap.tsx";
import { ChooseGitsAndVehiclesDialog } from "./ChooseGitsAndVehiclesDialog.tsx";
import { GangActions } from "./GangActions.tsx";
import { GangModelsList } from "./GangModelsList.tsx";
import { GangHeader } from "./GangView.tsx";
import { buildMode, gang as gangCss } from "./style.css.ts";
import { useGangContext } from "./useGang.ts";

function InitialStep() {
  const { gang } = useGangContext();
  const maxCost = gang.ruleset.constants.chefsRecMaxModelsCost;

  return (
    <StandaloneBuildStep
      stepLabel="Step 1"
      heading="Gather your gits!"
      text={
        <>Pick up to {maxCost} caps worth of naked gits and stock vehicles.</>
      }
      button={
        <DialogTrigger>
          <ChooseGitsAndVehiclesDialog />

          <DialogDisclosure className={buildMode.button}>
            Choose
          </DialogDisclosure>
        </DialogTrigger>
      }
    />
  );
}

function ChooseModelsStep() {
  const { gang } = useGangContext();
  const { baseCost, ruleset } = gang;
  const maxCost = ruleset.constants.chefsRecMaxModelsCost;

  return (
    <BuildStep
      done
      stepLabel="Step 1"
      heading="Gather your gits!"
      text={
        <>
          <strong>Done!</strong> Continue to <em className={lacquer}>step 2</em>{" "}
          or{" "}
          <DialogTrigger>
            <ChooseGitsAndVehiclesDialog />
            <DialogDisclosure className={interactiveText}>
              choose again
            </DialogDisclosure>
          </DialogTrigger>
          .
        </>
      }
      extra={
        <div style={{ marginInlineEnd: "-1rem" }}>
          <Cap
            cost={baseCost}
            label="Base Cost"
            additionalInfo={`Max ${maxCost}`}
          />
        </div>
      }
    >
      {!gang.mode.hasBeenRun && <GangModelsList />}
    </BuildStep>
  );
}

function ChefsRecResultIntro({ runCount }: { runCount: number }) {
  if (runCount > Gang.chefsRecRubbishRun) {
    return <>The Chef doesn&apos;t even care any more. Here is some rubbish.</>;
  }

  if (runCount === Gang.chefsRecRubbishRun) {
    return (
      <>
        The Chef prepared something <em className={lacquer}>really</em> special.
        You will love it ❤️.
      </>
    );
  }

  if (runCount > 30) {
    return (
      <>
        The Chef is getting a little tired of your{" "}
        <em className={lacquer}>s#!t</em>... Just. Pick. Something.
      </>
    );
  }

  if (runCount > 15) {
    return <>The Chef thinks that you could be a little more decisive.</>;
  }

  return <>The Chef prepared something special for your gang.</>;
}

function GenerateLoadoutStep() {
  const { gang } = useGangContext();
  const { mode } = gang;
  const { hasBeenRun } = mode;
  const containerRef = useRef<HTMLDivElement | null>(null);

  // If we went from not ready to ready, make sure to scroll this step into
  // view.
  const prevValueRef = useRef(hasBeenRun);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
    if (prevValueRef.current === false && hasBeenRun === true) {
      void sleep(200).then(() => {
        containerRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });
      });
    }

    prevValueRef.current = hasBeenRun;
  }, [hasBeenRun]);

  const runChefsRec = () => {
    const { data } = gang.runChefsRec();
    void database.gangs.updateOne(data);
  };
  return (
    <div className={buildMode.step} ref={containerRef}>
      <div className={buildMode.headerContent}>
        <div
          className={buildMode.stepLabel({
            done: hasBeenRun,
            align: "start",
          })}
        >
          Step 2
        </div>

        <Heading className={buildMode.heading}>Kit &apos;em out</Heading>

        {!hasBeenRun ? (
          <p className={buildMode.info}>
            Let the Chef recommend the best weapons, gear, treatments, mods, and
            repairs for your gang.
          </p>
        ) : (
          <>
            <p className={buildMode.info}>
              <strong>Done!</strong>{" "}
              <ChefsRecResultIntro runCount={mode.runCount} />
              <br />
              Continue to <em className={lacquer}>step 3</em> or{" "}
              <Button className={interactiveText} onClick={runChefsRec}>
                roll again!
              </Button>
            </p>
          </>
        )}
      </div>

      {!hasBeenRun ? (
        <Button
          className={button({
            size: "small",
            maxInlineSize: "auto",
          })}
          onClick={runChefsRec}
        >
          Roll them up!
        </Button>
      ) : (
        <GangModelsList />
      )}
    </div>
  );
}

export function ChefsRecView() {
  const { gang } = useGangContext();
  const { mode, models } = gang;

  return (
    <div className={gangCss.container}>
      <GangHeader />

      <HeadingLevel>
        {mode.isDone ? (
          <>
            <GangModelsList />
            <GangActions />
          </>
        ) : models.length === 0 ? (
          <InitialStep />
        ) : (
          <div className={buildMode.steps}>
            <ChooseModelsStep />

            <GenerateLoadoutStep />

            <WrapUpStep stepLabel="Step 3" />
          </div>
        )}
      </HeadingLevel>
    </div>
  );
}
